import React, { useState, useEffect, useRef, useMemo } from 'react';

interface EnginItem {
  id: string;
  numero: string;
  position: number;
}

interface EnginStatusManagerProps {
  engins: string[];
  formData: any;
  onUpdateStatus: (statusData: {
    gps: Record<string, string>;
    rdv: Record<string, string>;
    realise: Record<string, string>;
    validation: Record<string, string>;
    commentaires_gps: Record<string, string>;
    commentaires_rdv: Record<string, string>;
    commentaires_realise: Record<string, string>;
    commentaires_validation: Record<string, string>;
  }) => void;
  onUpdateFormData?: (field: keyof any, value: string) => void;
  disabled?: boolean;
}

// ===== Composant EnginStatusManager =====
const EnginStatusManager: React.FC<EnginStatusManagerProps> = ({ engins, formData, onUpdateStatus, disabled = false }) => {
  const enginItems: EnginItem[] = useMemo(() =>
    engins.map((numero, index) => ({
      id: `engin_${index + 1}`,
      numero,
      position: index
    })),
    [engins]
  );

  const [selectedEngin, setSelectedEngin] = useState<string>('');
  const [statusData, setStatusData] = useState({
    gps: {} as Record<string, string>,
    rdv: {} as Record<string, string>,
    realise: {} as Record<string, string>,
    validation: {} as Record<string, string>,
    commentaires_gps: {} as Record<string, string>,
    commentaires_rdv: {} as Record<string, string>,
    commentaires_realise: {} as Record<string, string>,
    commentaires_validation: {} as Record<string, string>
  });

  const isInitializedRef = useRef<boolean>(false);
  const lastEnginsRef = useRef<string[]>([]);
  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    const enginsChanged = JSON.stringify(lastEnginsRef.current) !== JSON.stringify(engins);

    if (!isInitializedRef.current || enginsChanged) {
      lastEnginsRef.current = engins;
      const currentFormData = formDataRef.current;
      try {
        const gpsData = currentFormData.veille_gps_donnees ? JSON.parse(currentFormData.veille_gps_donnees) : {};
        const rdvData = currentFormData.rdv_fait_donnees ? JSON.parse(currentFormData.rdv_fait_donnees) : {};
        const realiseData = currentFormData.realise_donnees ? JSON.parse(currentFormData.realise_donnees) : {};
        const validationData = currentFormData.validation_donnees ? JSON.parse(currentFormData.validation_donnees) : {};
        const commentairesGps = currentFormData.commentaires_gps_donnees ? JSON.parse(currentFormData.commentaires_gps_donnees) : {};
        const commentairesRdv = currentFormData.commentaires_rdv_donnees ? JSON.parse(currentFormData.commentaires_rdv_donnees) : {};
        const commentairesRealise = currentFormData.commentaires_realise_donnees ? JSON.parse(currentFormData.commentaires_realise_donnees) : {};
        const commentairesValidation = currentFormData.commentaires_validation_donnees ? JSON.parse(currentFormData.commentaires_validation_donnees) : {};

        const syncWithGlobalStatus = (enginData: Record<string, string>, globalStatus: string) => {
          const result: Record<string, string> = {};

          enginItems.forEach((enginItem) => {
            const existingValueById = enginData[enginItem.id];

            if (existingValueById) {
              result[enginItem.id] = existingValueById;
            } else {
              result[enginItem.id] = 'pending';
            }
          });

          return result;
        };

        const extractValidationComments = () => {
          const result: Record<string, string> = {};

          if (Object.keys(commentairesValidation).length > 0) {
            enginItems.forEach((enginItem) => {
              const valueById = commentairesValidation[enginItem.id];
              if (valueById) {
                result[enginItem.id] = valueById;
              }
            });
            return result;
          }

          const commentaires = currentFormData.commentaires || '';
          const refusalReason = currentFormData.motif_refus || '';

          const refusedEnginePattern = /Engins refusés:\s*(.+?)(?:\[|$)/;
          const match = commentaires.match(refusedEnginePattern);

          if (match) {
            const refusedSection = match[1];
            const engineReasons = refusedSection.split('|');

            engineReasons.forEach((reasonPair: string, parseIndex: number) => {
              const [enginNumero, reason] = reasonPair.split(':').map((s: string) => s?.trim());
              if (enginNumero && reason && parseIndex < enginItems.length) {

                const enginItem = enginItems[parseIndex];
                if (enginItem && !result[enginItem.id]) {
                  result[enginItem.id] = reason;
                }
              }
            });
          }

          if (refusalReason && Object.keys(result).length === 0) {
            enginItems.forEach(enginItem => {
              result[enginItem.id] = refusalReason;
            });
          }

          return result;
        };

        const finalGpsData = syncWithGlobalStatus(gpsData, currentFormData.veille_gps || '');
        const finalRdvData = syncWithGlobalStatus(rdvData, currentFormData.rdv_fait || '');
        const finalRealiseData = syncWithGlobalStatus(realiseData, currentFormData.realise || '');
        let finalValidationData = syncWithGlobalStatus(validationData, currentFormData.statut_validation || '');

        const commentairesLegacy = currentFormData.commentaires || '';
        const refusedEnginePattern = /Engins refusés:\s*(.+?)(?:\[|$)/;
        const refusedMatch = commentairesLegacy.match(refusedEnginePattern);

        if (refusedMatch) {
          const refusedSection = refusedMatch[1];
          const engineReasons = refusedSection.split('|');

          engineReasons.forEach((reasonPair: string, parseIndex: number) => {
            const [enginNumero] = reasonPair.split(':').map((s: string) => s?.trim());
            if (enginNumero && parseIndex < enginItems.length) {

              const enginItem = enginItems[parseIndex];
              if (enginItem) {
                finalValidationData[enginItem.id] = 'not_feasible';
              }
            }
          });
        }

        let finalValidationComments = commentairesValidation;
        if (Object.keys(commentairesValidation).length === 0) {
          const extractedValidationComments = extractValidationComments();
          if (Object.keys(extractedValidationComments).length > 0) {
            finalValidationComments = {
              ...extractedValidationComments,
              ...commentairesValidation
            };
          }
        }

        setStatusData({
          gps: finalGpsData,
          rdv: finalRdvData,
          realise: finalRealiseData,
          validation: finalValidationData,
          commentaires_gps: commentairesGps,
          commentaires_rdv: commentairesRdv,
          commentaires_realise: commentairesRealise,
          commentaires_validation: finalValidationComments
        });
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Error parsing status data:', error);
      }
    }
  }, [engins, enginItems]);

  const prevStatusDataRef = useRef<typeof statusData | null>(null);

  useEffect(() => {
    if (isInitializedRef.current) {
      const hasChanged = JSON.stringify(prevStatusDataRef.current) !== JSON.stringify(statusData);
      if (hasChanged) {
        onUpdateStatus(statusData);
        prevStatusDataRef.current = statusData;
      }
    }
  }, [statusData, onUpdateStatus]);

  const updateEnginStatus = (enginId: string, type: 'gps' | 'rdv' | 'realise' | 'validation', status: string) => {
    setStatusData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [enginId]: status
      }
    }));
  };

  const updateCommentaire = (enginId: string, type: 'commentaires_gps' | 'commentaires_rdv' | 'commentaires_realise' | 'commentaires_validation', value: string) => {
    setStatusData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [enginId]: value
      }
    }));
  };

  const getStatusColor = (enginId: string, type: 'gps' | 'rdv' | 'realise' | 'validation') => {
    const status = statusData[type][enginId];
    if (status === 'done') return 'bg-green-500';
    if (status === 'not_feasible') return 'bg-red-500';
    if (status === 'pending' || !status) return 'bg-yellow-500';
    return 'bg-yellow-500';
  };

  if (engins.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p>Aucun engin spécifié. Veuillez d'abord ajouter les numéros d'engins.</p>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-400 mb-4">
            <i className="fas fa-cogs mr-2"></i> Gestion par Engin
          </h3>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
          <i className="fas fa-ban text-orange-600 text-3xl mb-3"></i>
          <p className="text-orange-800 font-semibold mb-2">Intervention annulée par le client</p>
          <p className="text-orange-700 text-sm">La gestion par engin est désactivée pour cette intervention.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          <i className="fas fa-cogs mr-2"></i> Gestion par Engin
        </h3>

        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {enginItems.map((enginItem, idx) => (
            <button
              key={enginItem.id}
              type="button"
              onClick={() => setSelectedEngin(selectedEngin === enginItem.id ? '' : enginItem.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedEngin === enginItem.id
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {enginItem.numero}
            </button>
          ))}
        </div>
      </div>

      {selectedEngin && (() => {
        const selectedEnginItem = enginItems.find(e => e.id === selectedEngin);
        if (!selectedEnginItem) return null;

        return (
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="text-md font-semibold text-gray-800 mb-4 text-center">
              Statut pour l'engin: <span className="text-blue-600">{selectedEnginItem.numero}</span>
            </h4>

            {statusData.validation[selectedEnginItem.id] === 'not_feasible' && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-center">
                <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
                <span className="text-red-700 font-medium">
                  Cet engin est refusé. Changez son statut de validation pour accéder aux autres colonnes.
                </span>
              </div>
            )}

            <div className={`grid gap-4 ${statusData.validation[selectedEnginItem.id] === 'not_feasible' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
              <div className="text-center">
                <h5 className="font-medium text-gray-700 mb-2">
                  <i className="fas fa-check-double mr-1"></i> Validation SR
                </h5>
                <div className="space-y-2">
                  <div>
                    <button
                      type="button"
                      onClick={() => updateEnginStatus(selectedEnginItem.id, 'validation', 'done')}
                      className={`px-3 py-1 rounded text-sm ${statusData.validation[selectedEnginItem.id] === 'done'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                        }`}
                    >
                      ✓ Validé
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => updateEnginStatus(selectedEnginItem.id, 'validation', 'not_feasible')}
                      className={`px-3 py-1 rounded text-sm ${statusData.validation[selectedEnginItem.id] === 'not_feasible'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                        }`}
                    >
                      ✗ Refusé
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => updateEnginStatus(selectedEnginItem.id, 'validation', 'pending')}
                      className={`px-3 py-1 rounded text-sm ${statusData.validation[selectedEnginItem.id] === 'pending' || !statusData.validation[selectedEnginItem.id]
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                        }`}
                    >
                      ⧖ En attente
                    </button>
                  </div>
                </div>
                {statusData.validation[selectedEnginItem.id] === 'not_feasible' && (
                  <div className="mt-2">
                    <textarea
                      value={statusData.commentaires_validation[selectedEnginItem.id] || ''}
                      onChange={(e) => updateCommentaire(selectedEnginItem.id, 'commentaires_validation', e.target.value)}
                      rows={2}
                      className="form-input text-xs w-full"
                      placeholder="Motif du refus..."
                    />
                  </div>
                )}
              </div>

              {statusData.validation[selectedEnginItem.id] !== 'not_feasible' && (
                <div className="text-center">
                  <h5 className="font-medium text-gray-700 mb-2">
                    <i className="fas fa-satellite mr-1"></i> Veille GPS
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'gps', 'done')}
                        className={`px-3 py-1 rounded text-sm ${statusData.gps[selectedEnginItem.id] === 'done'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✓ Fait
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'gps', 'not_feasible')}
                        className={`px-3 py-1 rounded text-sm ${statusData.gps[selectedEnginItem.id] === 'not_feasible'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✗ Pas faisable
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'gps', 'pending')}
                        className={`px-3 py-1 rounded text-sm ${statusData.gps[selectedEnginItem.id] === 'pending' || !statusData.gps[selectedEnginItem.id]
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ⧖ En attente
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <textarea
                      value={statusData.commentaires_gps[selectedEnginItem.id] || ''}
                      onChange={(e) => updateCommentaire(selectedEnginItem.id, 'commentaires_gps', e.target.value)}
                      rows={2}
                      className="form-input text-xs w-full"
                      placeholder="Commentaire pour cet engin..."
                    />
                  </div>
                </div>
              )}

              {statusData.validation[selectedEnginItem.id] !== 'not_feasible' && (
                <div className="text-center">
                  <h5 className="font-medium text-gray-700 mb-2">
                    <i className="fas fa-handshake mr-1"></i> RDV Fait
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'rdv', 'done')}
                        className={`px-3 py-1 rounded text-sm ${statusData.rdv[selectedEnginItem.id] === 'done'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✓ Fait
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'rdv', 'not_feasible')}
                        className={`px-3 py-1 rounded text-sm ${statusData.rdv[selectedEnginItem.id] === 'not_feasible'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✗ Pas faisable
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'rdv', 'pending')}
                        className={`px-3 py-1 rounded text-sm ${statusData.rdv[selectedEnginItem.id] === 'pending' || !statusData.rdv[selectedEnginItem.id]
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ⧖ En attente
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <textarea
                      value={statusData.commentaires_rdv[selectedEnginItem.id] || ''}
                      onChange={(e) => updateCommentaire(selectedEnginItem.id, 'commentaires_rdv', e.target.value)}
                      rows={2}
                      className="form-input text-xs w-full"
                      placeholder="Commentaire pour cet engin..."
                    />
                  </div>
                </div>
              )}

              {statusData.validation[selectedEnginItem.id] !== 'not_feasible' && (
                <div className="text-center">
                  <h5 className="font-medium text-gray-700 mb-2">
                    <i className="fas fa-check-double mr-1"></i> Réalisé
                  </h5>
                  <div className="space-y-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'realise', 'done')}
                        className={`px-3 py-1 rounded text-sm ${statusData.realise[selectedEnginItem.id] === 'done'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✓ Fait
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'realise', 'not_feasible')}
                        className={`px-3 py-1 rounded text-sm ${statusData.realise[selectedEnginItem.id] === 'not_feasible'
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ✗ Pas faisable
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => updateEnginStatus(selectedEnginItem.id, 'realise', 'pending')}
                        className={`px-3 py-1 rounded text-sm ${statusData.realise[selectedEnginItem.id] === 'pending' || !statusData.realise[selectedEnginItem.id]
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-200 text-gray-700'
                          }`}
                      >
                        ⧖ En attente
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <textarea
                      value={statusData.commentaires_realise[selectedEnginItem.id] || ''}
                      onChange={(e) => updateCommentaire(selectedEnginItem.id, 'commentaires_realise', e.target.value)}
                      rows={2}
                      className="form-input text-xs w-full"
                      placeholder="Commentaire pour cet engin..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="bg-white p-4 rounded-lg border">
        <h5 className="font-medium text-gray-700 mb-3 text-center">Aperçu des statuts</h5>
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">Validation</h6>
            <div className="flex flex-wrap justify-center gap-1">
              {enginItems.map((enginItem) => (
                <span
                  key={enginItem.id}
                  className={`inline-block w-3 h-3 rounded-full ${getStatusColor(enginItem.id, 'validation')} mr-1`}
                  title={`${enginItem.numero}: ${statusData.validation[enginItem.id] || 'En attente'}`}
                ></span>
              ))}
            </div>
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">GPS</h6>
            <div className="flex flex-wrap justify-center gap-1">
              {enginItems.map((enginItem) => (
                <span
                  key={enginItem.id}
                  className={`inline-block w-3 h-3 rounded-full ${getStatusColor(enginItem.id, 'gps')} mr-1`}
                  title={`${enginItem.numero}: ${statusData.gps[enginItem.id] || 'En attente'}`}
                ></span>
              ))}
            </div>
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">RDV</h6>
            <div className="flex flex-wrap justify-center gap-1">
              {enginItems.map((enginItem) => (
                <span
                  key={enginItem.id}
                  className={`inline-block w-3 h-3 rounded-full ${getStatusColor(enginItem.id, 'rdv')} mr-1`}
                  title={`${enginItem.numero}: ${statusData.rdv[enginItem.id] || 'En attente'}`}
                ></span>
              ))}
            </div>
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">Réalisé</h6>
            <div className="flex flex-wrap justify-center gap-1">
              {enginItems.map((enginItem) => (
                <span
                  key={enginItem.id}
                  className={`inline-block w-3 h-3 rounded-full ${getStatusColor(enginItem.id, 'realise')} mr-1`}
                  title={`${enginItem.numero}: ${statusData.realise[enginItem.id] || 'En attente'}`}
                ></span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnginStatusManager;
